import { parseCompetencyMap } from './foundationalSchema';
import { FLNDomain, FLNLakshya, getFLNConfig, FLN_CLASS_CONFIGS } from './flnSchema';
import type { AnalyticsStudentRecord } from '../../../services/holisticApi';

export interface LakshyaStats {
  code: string;
  domain: FLNDomain;
  isHindi: boolean;
  shortLabel: string;
  fullLabel: string;
  // Entry (T1)
  entryBeginner: number;
  entryProgressing: number;
  entryProficient: number;
  entryTotal: number;
  entryProficientPct: number;
  // Exit (T2)
  exitBeginner: number;
  exitProgressing: number;
  exitProficient: number;
  exitTotal: number;
  exitProficientPct: number;
  // Movement
  movementPct: number; // exitProficientPct - entryProficientPct
}

export interface FLNDomainSummary {
  domain: FLNDomain;
  entryProficientPct: number;
  exitProficientPct: number;
  movementPct: number;
  lakshyas: LakshyaStats[];
}

export interface FLNClassSummary {
  classId: number;
  classLabel: string;
  totalStudents: number;
  entryProficientPct: number; // overall % across all Lakshyas
  exitProficientPct: number;
  movementPct: number;
  domains: FLNDomainSummary[];
}

// ── rubric score for a single indicator in a given term map ─────────────────
const getRubricScore = (
  map: Record<string, number>,
  key: string,
): 0 | 1 | 2 | 3 => {
  const v = map[key];
  if (v === 1 || v === 2 || v === 3) return v as 1 | 2 | 3;
  return 0;
};

const pct = (n: number, total: number) => total ? +((n / total) * 100).toFixed(1) : 0;

// ── compute stats for a single Lakshya across an array of student records ───
const computeLakshyaStats = (
  lakshya: FLNLakshya,
  records: AnalyticsStudentRecord[],
): LakshyaStats => {
  let eB = 0, eP = 0, ePr = 0, eTotal = 0;
  let xB = 0, xP = 0, xPr = 0, xTotal = 0;

  records.forEach(r => {
    const m1 = parseCompetencyMap(r.term1_competency) as Record<string, number>;
    const m2 = parseCompetencyMap(r.term2_competency) as Record<string, number>;

    const s1 = getRubricScore(m1, lakshya.competencyKey);
    const s2 = getRubricScore(m2, lakshya.competencyKey);

    if (s1 > 0) {
      eTotal++;
      if (s1 === 3) ePr++; else if (s1 === 2) eP++; else eB++;
    }
    if (s2 > 0) {
      xTotal++;
      if (s2 === 3) xPr++; else if (s2 === 2) xP++; else xB++;
    }
  });

  const entryProficientPct = pct(ePr, eTotal);
  const exitProficientPct  = pct(xPr, xTotal);

  return {
    code:             lakshya.code,
    domain:           lakshya.domain,
    isHindi:          lakshya.isHindi,
    shortLabel:       lakshya.shortLabel,
    fullLabel:        lakshya.fullLabel,
    entryBeginner:    eB,
    entryProgressing: eP,
    entryProficient:  ePr,
    entryTotal:       eTotal,
    entryProficientPct,
    exitBeginner:     xB,
    exitProgressing:  xP,
    exitProficient:   xPr,
    exitTotal:        xTotal,
    exitProficientPct,
    movementPct:      +(exitProficientPct - entryProficientPct).toFixed(1),
  };
};

// ── main: compute FLN data for a class (requires per-student rubric maps) ───
export const computeFLNClassSummary = (
  records: AnalyticsStudentRecord[],
  classId: number,
): FLNClassSummary | null => {
  const config = getFLNConfig(classId);
  if (!config) return null;

  const classRecords = records.filter(r => Number(r.class_id) === classId);
  if (!classRecords.length) return null;

  // Check that rubric maps exist (class-level records)
  const hasRubricData = classRecords.some(r =>
    Object.keys(parseCompetencyMap(r.term1_competency)).length > 0 ||
    Object.keys(parseCompetencyMap(r.term2_competency)).length > 0,
  );
  if (!hasRubricData) return null;

  const domainMap = new Map<FLNDomain, LakshyaStats[]>();
  config.lakshyas.forEach(lakshya => {
    const stats = computeLakshyaStats(lakshya, classRecords);
    if (!domainMap.has(lakshya.domain)) domainMap.set(lakshya.domain, []);
    domainMap.get(lakshya.domain)!.push(stats);
  });

  const domains: FLNDomainSummary[] = Array.from(domainMap.entries()).map(([domain, lakshyas]) => {
    const validEntry = lakshyas.filter(l => l.entryTotal > 0);
    const validExit  = lakshyas.filter(l => l.exitTotal  > 0);
    const domainEntryPct = validEntry.length
      ? +(validEntry.reduce((s, l) => s + l.entryProficientPct, 0) / validEntry.length).toFixed(1)
      : 0;
    const domainExitPct = validExit.length
      ? +(validExit.reduce((s, l) => s + l.exitProficientPct, 0) / validExit.length).toFixed(1)
      : 0;
    return { domain, entryProficientPct: domainEntryPct, exitProficientPct: domainExitPct, movementPct: +(domainExitPct - domainEntryPct).toFixed(1), lakshyas };
  });

  const allLakshyas = domains.flatMap(d => d.lakshyas);
  const validEntryAll = allLakshyas.filter(l => l.entryTotal > 0);
  const validExitAll  = allLakshyas.filter(l => l.exitTotal  > 0);
  const overallEntry  = validEntryAll.length ? +(validEntryAll.reduce((s, l) => s + l.entryProficientPct, 0) / validEntryAll.length).toFixed(1) : 0;
  const overallExit   = validExitAll.length  ? +(validExitAll.reduce((s, l)  => s + l.exitProficientPct,  0) / validExitAll.length).toFixed(1)  : 0;

  return {
    classId,
    classLabel:          config.label,
    totalStudents:       classRecords.length,
    entryProficientPct:  overallEntry,
    exitProficientPct:   overallExit,
    movementPct:         +(overallExit - overallEntry).toFixed(1),
    domains,
  };
};

// ── compute across ALL Foundation classes in a record set ───────────────────
export const computeFLNAllClasses = (
  records: AnalyticsStudentRecord[],
): FLNClassSummary[] => {
  const allClassIds = [-3, -2, -1, 1, 2];
  return allClassIds
    .map(id => computeFLNClassSummary(records, id))
    .filter((s): s is FLNClassSummary => s !== null);
};
